import React from 'react'

import {
  connectorsForWallets,
  Wallet,
  WalletDetailsParams,
} from '@rainbow-me/rainbowkit'
import {
  metaMaskWallet,
  rabbyWallet,
  coinbaseWallet,
  walletConnectWallet,
  ledgerWallet,
} from '@rainbow-me/rainbowkit/wallets'
import {
  Chain,
  mainnet,
  base,
  polygon,
  sepolia,
  baseSepolia,
  polygonAmoy,
} from 'wagmi/chains'
import { universalWalletConnector } from '@magiclabs/wagmi-connector'
import {
  Config,
  createConfig,
  createConnector as createWagmiConnector,
  http,
  WagmiProvider,
} from 'wagmi'

/**
 * Called to init wagmi.
 *
 * This code lives in a helper library and each parent app has a different way of injecting config hence
 * this function is called from the parent app.
 *
 * You MUST call this before <ConnectWalletContextProvider> is rendered.
 */
export const initWalletConfig = (
  alchemyAPIKey: string,
  walletconnectProjectId: string,
  magicLinkKey: string | null
) => {
  const chains = [
    mainnet,
    polygon,
    base,
    sepolia,
    baseSepolia,
    polygonAmoy,
  ] as const

  const chainUrls = {
    [mainnet.id]: `https://eth-mainnet.g.alchemy.com/v2/${alchemyAPIKey}`,
    [polygon.id]: `https://polygon-mainnet.g.alchemy.com/v2/${alchemyAPIKey}`,
    [base.id]: `https://base-mainnet.g.alchemy.com/v2/${alchemyAPIKey}`,
    [sepolia.id]: `https://eth-sepolia.g.alchemy.com/v2/${alchemyAPIKey}`,
    [baseSepolia.id]: `https://base-sepolia.g.alchemy.com/v2/${alchemyAPIKey}`,
    [polygonAmoy.id]: `https://polygon-amoy.g.alchemy.com/v2/${alchemyAPIKey}`,
  }

  const transports = {
    [mainnet.id]: http(chainUrls[mainnet.id]),
    [polygon.id]: http(chainUrls[polygon.id]),
    [base.id]: http(chainUrls[base.id]),
    [sepolia.id]: http(chainUrls[sepolia.id]),
    [baseSepolia.id]: http(chainUrls[baseSepolia.id]),
    [polygonAmoy.id]: http(chainUrls[polygonAmoy.id]),
  }

  const magicWallet = ({
    chains,
    apiKey,
  }: {
    chains: readonly Chain[]
    apiKey: string
  }) => {
    const fn = (): Wallet => ({
      id: 'magic',
      name: 'Magic Link',
      iconUrl:
        'https://verisart-marketing-production.s3.amazonaws.com/logos/1-Icon_Magic_Color.svg',
      iconBackground: 'white',
      installed: true,
      createConnector: (walletDetails: WalletDetailsParams) =>
        createWagmiConnector((config) => ({
          ...universalWalletConnector({
            chains,
            options: {
              apiKey,
              magicSdkConfiguration: {
                network: {
                  rpcUrl: chainUrls[mainnet.id],
                  chainId: mainnet.id,
                },
              },
              networks: [
                {
                  rpcUrl: chainUrls[mainnet.id],
                  chainId: mainnet.id,
                },
                {
                  rpcUrl: chainUrls[polygon.id],
                  chainId: polygon.id,
                },
                {
                  rpcUrl: chainUrls[base.id],
                  chainId: base.id,
                },
                {
                  rpcUrl: chainUrls[sepolia.id],
                  chainId: sepolia.id,
                },
              ],
            },
          })(config),
          ...walletDetails,
        })),
    })
    return fn
  }

  const coinbaseWalletConnector = coinbaseWallet
  coinbaseWalletConnector.preference = 'eoaOnly'

  const wallets = [
    ...(magicLinkKey ? [magicWallet({ chains, apiKey: magicLinkKey })] : []),
    metaMaskWallet,
    rabbyWallet,
    coinbaseWalletConnector,
    ledgerWallet,
    walletConnectWallet,
  ]

  const connectors = connectorsForWallets(
    [
      {
        groupName: 'Recommended',
        wallets,
      },
    ],
    {
      projectId: walletconnectProjectId,
      walletConnectParameters: {
        qrModalOptions: {
          themeVariables: {
            // Ensure the modal appears above our modal
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            '--w3m-z-index': '9999999',

            // So weirdly, the TS bindings have the new CSS variable name (see above)
            // but for some reason this wasn't working yet. We're setting the old one here too to be safe.
            '--wcm-z-index': '9999999',
          },
        },
      },
    }
  )

  return createConfig({
    connectors,
    multiInjectedProviderDiscovery: true,
    chains: chains,
    transports,
    ssr: true,
  })
}

const ConnectWalletContextProvider = ({
  children,
  config,
}: {
  children: React.ReactNode
  config: Config
}) => {
  return <WagmiProvider config={config}>{children}</WagmiProvider>
}

export default ConnectWalletContextProvider
